import * as React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { diffWordsWithSpace } from 'diff';
import './sentence-transform-exercise.css';

const GREEN = 'rgba(19,130,117,1)';

export default function SentenceTransformPreviewStyled({ questions = [], title = 'EXERCISE PREVIEW' }) {

  const parseAndUnderline = (text) => {
    if (!text) return null;
    const parts = text.split(/<\/?u>/);
    return parts.map((part, i) =>
      i % 2 === 1
        ? <span key={i} className="custom-underline">{part}</span>
        : <span key={i}>{part}</span>
    );
  };

  const normaliseCorrectAnswer = (input = '') => {
    const trimmed = input.trim();
    return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
  };

  const convertNewlinesToBr = (text = '') => {
    const clean = text.replace(/<[^>]*>/g, '');
    const parts = clean.split('\n');
    return parts.map((line, idx) => (
      <React.Fragment key={idx}>
        {line}
        {idx < parts.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Show blue vs red differences
  const getDifferences = (line1, line2) => {
    const expected = normaliseCorrectAnswer(line2);
    if (!line1 || !expected) return <span />;
    const differences = diffWordsWithSpace(line1, expected);
    return differences.map((part, idx) => {
      const style = {
        color: part.added ? '#1976d2' : part.removed ? '#ef5350' : '#000',
        textDecoration: part.added ? 'underline' : part.removed ? 'line-through' : 'none',
        fontWeight: (part.added || part.removed) ? 'bold' : 'normal',
      };
      const textContent = (part.added || part.removed) ? ` ${part.value}` : part.value;
      return <span key={idx} style={style}>{convertNewlinesToBr(textContent)}</span>;
    });
  };

  // Replace each <u>…</u> in the question with each <a>…</a> from correctedAnswer, then diff
  const answerTextRedOrBlue = (questionWithBlanks, correctedAnswer) => {
    if (!questionWithBlanks || !correctedAnswer) return null;

    const answers = [];
    const regexA = /<a>(.*?)<\/a>/g;
    let match;
    while ((match = regexA.exec(correctedAnswer)) !== null) {
      answers.push(match[1]);
    }

    let idx = 0;
    const replaced = questionWithBlanks.replace(/<u>.*?<\/u>/g, () => answers[idx++] || '');
    const questionClean = questionWithBlanks.replaceAll('<u>', '').replaceAll('</u>', '');
    return getDifferences(questionClean, replaced);
  };

  return (
    <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: GREEN, mb: 1 }}>
        {title}
      </Typography>

          {questions.map((q, i) => (
            <Box key={q.questionId || `q-${i}`} sx={{ pb: 1 }}>
              {/* Q circle + original sentence */}
              <Box display="flex" alignItems="top" mb={0.5}>
                <Box className="qna-circle" mt={0.5} >
                  <Typography
                    sx={{
                      color: 'rgba(0,0,0,.6)',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      minWidth: '1.8rem',
                      textAlign: 'center',
                    }}
                  >
                    Q
                  </Typography>
                </Box>
                <span style={{ fontWeight: 'bold' }}>{q.originalSentence}</span>
              </Box>

              {/* Correct / Wrong section */}
              {q.status === 1 && (
                <Box display="flex" alignItems="top" mb={0.5}>
                  <Box className="qna-circle" mt={0.5} >
                    <CheckIcon sx={{ mx: '.2rem', color: GREEN }} />
                  </Box>
                  <span style={{ fontWeight: 'bold' }}>{parseAndUnderline(q.question)}</span>
                </Box>
              )}

              {q.status === 3 && (
                <Box display="flex" alignItems="top" mb={0.5}>
                  <Box className="qna-circle" mt={0.5}>
                    <CloseIcon sx={{ mx: '.2rem', color: 'red' }} />
                  </Box>
                  <Box>{answerTextRedOrBlue(q.question, q.correctedAnswer)}</Box>
                </Box>
              )}

              <Divider sx={{ mt: 1 }} />
            </Box>
          ))}
        </Box>
    </Box>
  );
}
